"use client";
import { useState, useEffect } from "react";
import { exportBookingIcs, fetchDayAvailability, fetchWeekAvailability } from "../../lib/scheduleApi";
import { Availability } from "../../types/schedule";
import { Clock, Timer, Mic, Hourglass, AlertTriangle, Calendar, Printer, Edit, Lightbulb, Circle } from "../icons";

export function StudioSidebar() {
  const [blackoutDate, setBlackoutDate] = useState("");
  const [blackoutReason, setBlackoutReason] = useState("");
  const [weekFull, setWeekFull] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<"day" | "week" | "month">("day");
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [studioId] = useState("studio-1");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Availability | null>(null);
  const [editForm, setEditForm] = useState({
    startTime: '',
    endTime: '',
    reason: '',
    status: 'open' as 'open' | 'blocked' | 'booked'
  });

  const loadAvailability = async () => {
    try {
      if (viewMode === "day") {
        const data = await fetchDayAvailability(studioId, currentDate);
        setAvailability(data);
      } else if (viewMode === "week") {
        const data = await fetchWeekAvailability(studioId, currentDate);
        setAvailability(data);
      }
    } catch (error) {
      console.error("Error loading availability:", error);
    }
  };

  useEffect(() => {
    loadAvailability();
  }, [viewMode, currentDate, studioId]);

  const handleAddBlackout = () => {
    if (!blackoutDate || !blackoutReason) {
      alert("Vul datum en reden in");
      return;
    }
    // Implement blackout logic
    console.log("Adding blackout:", { date: blackoutDate, reason: blackoutReason });
    setBlackoutDate("");
    setBlackoutReason("");
  };

  const handleExportIcs = () => {
    // Stub implementation
    exportBookingIcs({});
    alert("ICS export gestart");
  };

  const handleSlotClick = (slot: Availability) => {
    setEditingSlot(slot);
    
    // Format time for input fields
    const formatTimeForInput = (timestamp: any) => {
      if (timestamp && timestamp.toDate) {
        return timestamp.toDate().toLocaleTimeString("nl-NL", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        });
      }
      if (timestamp instanceof Date) {
        return timestamp.toLocaleTimeString("nl-NL", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        });
      }
      return "00:00";
    };

    setEditForm({
      startTime: formatTimeForInput(slot.start),
      endTime: formatTimeForInput(slot.end),
      reason: slot.reason || '',
      status: slot.status as 'open' | 'blocked' | 'booked'
    });
    
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingSlot) return;

    try {
      // Create new start and end times
      const newStart = new Date(`${currentDate.toISOString().split('T')[0]}T${editForm.startTime}`);
      const newEnd = new Date(`${currentDate.toISOString().split('T')[0]}T${editForm.endTime}`);

      // Update the slot in demo data
      if (window.confirm("Tijdvak bijwerken?")) {
        // This would normally call an API to update the slot
        console.log("Updating slot:", {
          original: editingSlot,
          new: { start: newStart, end: newEnd, reason: editForm.reason, status: editForm.status }
        });
        
        setShowEditModal(false);
        setEditingSlot(null);
        await loadAvailability();
        alert("Tijdvak succesvol bijgewerkt!");
      }
    } catch (error) {
      console.error("Error updating slot:", error);
      alert("Kon tijdvak niet bijwerken");
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingSlot(null);
    setEditForm({ startTime: '', endTime: '', reason: '', status: 'open' });
  };

  return (
    <aside className="w-80 border-l p-4 space-y-4 bg-gray-50">
      <h3 className="font-semibold text-lg">Studio Instellingen</h3>
      
      {/* Studio Config Display */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-2">Studio Configuratie</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <div className="flex items-center gap-2">
            <Clock size={14} strokeWidth={1.5} />
            <span>Open: 09:00 - 21:00</span>
          </div>
          <div className="flex items-center gap-2">
            <Timer size={14} strokeWidth={1.5} />
            <span>Slotduur: 90 min</span>
          </div>
          <div className="flex items-center gap-2">
            <Mic size={14} strokeWidth={1.5} />
            <span>Opname: 60 min</span>
          </div>
          <div className="flex items-center gap-2">
            <Hourglass size={14} strokeWidth={1.5} />
            <span>Buffer: 30 min</span>
          </div>
        </div>
      </div>

      {/* Blackout Section */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Blackout Toevoegen</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={blackoutDate}
              onChange={(e) => setBlackoutDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reden
            </label>
            <input
              type="text"
              value={blackoutReason}
              onChange={(e) => setBlackoutReason(e.target.value)}
              placeholder="Bijv. Studio onderhoud"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <button 
            onClick={handleAddBlackout}
            className="w-full px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Blackout Toevoegen
          </button>
        </div>
      </div>

      {/* Week Full Toggle */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Week Instellingen</h4>
        <label className="flex items-center gap-3">
          <input 
            type="checkbox" 
            checked={weekFull}
            onChange={(e) => setWeekFull(e.target.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm">Week vol (geen nieuwe boekingen)</span>
        </label>
        {weekFull && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 flex items-center gap-2">
            <AlertTriangle size={14} strokeWidth={1.5} />
            <span>Bestaande boekingen blijven staan</span>
          </div>
        )}
      </div>

      {/* Export Section */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Export & Tools</h4>
        <div className="space-y-2">
          <button 
            onClick={handleExportIcs}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            <Calendar size={16} strokeWidth={1.5} className="mr-2" />
            Export ICS
          </button>
          <button 
            onClick={() => window.print()}
            className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors flex items-center justify-center"
          >
            <Printer size={16} strokeWidth={1.5} className="mr-2" />
            Print Schema
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Snelle Statistieken</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Open slots:</span>
            <span className="font-medium text-green-600">12</span>
          </div>
          <div className="flex justify-between">
            <span>Geboekt:</span>
            <span className="font-medium text-blue-600">8</span>
          </div>
          <div className="flex justify-between">
            <span>Geblokkeerd:</span>
            <span className="font-medium text-gray-600">3</span>
          </div>
          <div className="flex justify-between">
            <span>Bezetting:</span>
            <span className="font-medium text-purple-600">67%</span>
          </div>
        </div>
      </div>

      {/* Tijdvakken Overzicht */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Tijdvakken Overzicht</h4>
        
        {/* View Mode Toggle */}
        <div className="mb-3">
          <div className="flex border rounded-lg">
            <button 
              className={`px-2 py-1 text-xs font-medium ${
                viewMode === "day" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("day")}
            >
              Dag
            </button>
            <button 
              className={`px-2 py-1 text-xs font-medium ${
                viewMode === "week" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("week")}
            >
              Week
            </button>
            <button 
              className={`px-2 py-1 text-xs font-medium ${
                viewMode === "month" ? "bg-purple-500 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setViewMode("month")}
            >
              Maand
            </button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="mb-3 flex items-center gap-2">
          <button 
            className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
            onClick={() => {
              const newDate = new Date(currentDate);
              if (viewMode === "day") newDate.setDate(newDate.getDate() - 1);
              else if (viewMode === "week") newDate.setDate(newDate.getDate() - 7);
              else newDate.setMonth(newDate.getMonth() - 1);
              setCurrentDate(newDate);
            }}
          >
            ←
          </button>
          <span className="px-2 py-1 text-xs font-medium text-center flex-1">
            {currentDate.toLocaleDateString("nl-NL", {
              day: "numeric",
              month: "short",
              year: "numeric"
            })}
          </span>
          <button 
            className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
            onClick={() => {
              const newDate = new Date(currentDate);
              if (viewMode === "day") newDate.setDate(newDate.getDate() + 1);
              else if (viewMode === "week") newDate.setDate(newDate.getDate() + 7);
              else newDate.setMonth(newDate.getMonth() + 1);
              setCurrentDate(newDate);
            }}
          >
            →
          </button>
        </div>

        {/* Time Slots List */}
        <div className="max-h-64 overflow-y-auto">
          {availability.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <Calendar size={32} strokeWidth={1.5} className="mx-auto mb-2 text-gray-400" />
              <p className="text-xs">Geen tijdvakken</p>
            </div>
          ) : (
            <div className="space-y-1">
              {availability.map((slot, index) => {
                const formatTime = (timestamp: any) => {
                  if (timestamp && timestamp.toDate) {
                    return timestamp.toDate().toLocaleTimeString("nl-NL", {
                      hour: "2-digit",
                      minute: "2-digit"
                    });
                  }
                  if (timestamp instanceof Date) {
                    return timestamp.toLocaleTimeString("nl-NL", {
                      hour: "2-digit",
                      minute: "2-digit"
                    });
                  }
                  return "00:00";
                };

                const getSlotColor = (status: string) => {
                  switch (status) {
                    case "open": return "bg-green-100 border-green-300 text-green-800";
                    case "booked": return "bg-blue-100 border-blue-300 text-blue-800";
                    case "blocked": return "bg-red-100 border-red-300 text-red-800";
                    default: return "bg-gray-100 border-gray-300 text-gray-600";
                  }
                };

                return (
                  <div
                    key={index}
                    className={`p-2 border rounded text-xs cursor-pointer hover:shadow-sm transition-shadow ${getSlotColor(slot.status)}`}
                    onClick={() => handleSlotClick(slot)}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {formatTime(slot.start)} - {formatTime(slot.end)}
                      </span>
                      <span className="text-xs">
                        {slot.status === "open" ? (
                          <Circle size={12} strokeWidth={2} className="text-green-500 fill-green-500" />
                        ) : slot.status === "booked" ? (
                          <Circle size={12} strokeWidth={2} className="text-blue-500 fill-blue-500" />
                        ) : slot.status === "blocked" ? (
                          <Circle size={12} strokeWidth={2} className="text-red-500 fill-red-500" />
                        ) : (
                          <Circle size={12} strokeWidth={2} className="text-gray-400 fill-gray-400" />
                        )}
                      </span>
                    </div>
                    {slot.reason && (
                      <div className="text-xs text-gray-600 mt-1 truncate">
                        {slot.reason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-white p-4 rounded-lg border">
        <h4 className="font-medium mb-3">Help & Tips</h4>
        <div className="text-xs text-gray-600 space-y-2">
          <div>• Klik op tijdvakken om te bewerken</div>
          <div>• Gebruik week vol voor vakanties</div>
          <div>• Blackouts blokkeren hele dagen</div>
          <div>• Buffer tijd is automatisch inbegrepen</div>
        </div>
      </div>

      {/* Edit Slot Modal */}
      {showEditModal && editingSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Edit size={20} strokeWidth={1.5} />
              Tijdvak Bewerken
            </h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starttijd *</label>
                  <input
                    type="time"
                    value={editForm.startTime}
                    onChange={(e) => setEditForm({...editForm, startTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eindtijd *</label>
                  <input
                    type="time"
                    value={editForm.endTime}
                    onChange={(e) => setEditForm({...editForm, endTime: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value as 'open' | 'blocked' | 'booked'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="open">Open</option>
                  <option value="blocked">Geblokkeerd</option>
                  <option value="booked">Geboekt</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reden (optioneel)</label>
                <input
                  type="text"
                  value={editForm.reason}
                  onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Bijv. Pauze, Onderhoud, Privé afspraak"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1 flex items-center gap-2">
                    <Lightbulb size={14} strokeWidth={1.5} />
                    Huidige tijdvak:
                  </p>
                  <p>{editingSlot.start && editingSlot.start.toDate ? 
                    editingSlot.start.toDate().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : 
                    "00:00"} - {editingSlot.end && editingSlot.end.toDate ? 
                    editingSlot.end.toDate().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : 
                    "00:00"}</p>
                  <p className="text-xs mt-1">Status: {editingSlot.status}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleEditSave}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                Bijwerken
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
